import { createContext, useContext, useEffect, useState, useCallback } from "react";

// ============================================================
// Pembagian Keuntungan Organisasi di level Gerai (model bisnis Bagian 5.2).
// Dari jatah keuntungan yang masuk ke Gerai, dipecah ke kategori berikut
// (default 5 kategori, total wajib 100%). Kategori dapat ditambah/dihapus
// admin (CRUD) — meta di bawah hanya untuk kategori bawaan; kategori baru
// memakai meta default lewat metaFor().
// ============================================================

export const featureMeta = {
  pengembangan_usaha: {
    name: "Pengembangan Usaha",
    desc: "Untuk pengembangan usaha koperasi di Gerai tersebut",
    icon: "🏗️",
    category: "Internal",
    defaultMemberShare: false,
  },
  dana_sosial: {
    name: "Dana Sosial",
    desc: "Dana sosial koperasi di Gerai tersebut",
    icon: "❤️",
    category: "Sosial",
    defaultMemberShare: false,
  },
  bonus_karyawan: {
    name: "Bonus Karyawan",
    desc: "Dibagi rata ke seluruh karyawan Gerai",
    icon: "🤝",
    category: "Internal",
    defaultMemberShare: false,
  },
  bonus_pimpinan_gerai: {
    name: "Bonus Pimpinan Gerai",
    desc: "Bonus untuk pimpinan di Gerai tersebut",
    icon: "👤",
    category: "Internal",
    defaultMemberShare: false,
  },
  shu_anggota: {
    name: "SHU Anggota",
    desc: "Dibagikan ke anggota: 30% jasa modal (proporsi simpanan) + 70% jasa usaha (proporsi transaksi)",
    icon: "💰",
    category: "Anggota",
    defaultMemberShare: true,
  },
};

// Meta untuk kategori kustom yang ditambah admin (tidak ada di featureMeta).
export const metaFor = (key) => featureMeta[key] || {
  name: key,
  desc: "Kategori pembagian keuntungan tambahan",
  icon: "📌",
  category: "Internal",
  defaultMemberShare: false,
};

// Default sesuai dokumen model bisnis final (total 100%).
export const DEFAULT_ORG_PROFIT_CONFIG = [
  { feature_key: "pengembangan_usaha", display_name: "Pengembangan Usaha", is_active: true, percentage_allocation: 30.0, is_member_share: false },
  { feature_key: "dana_sosial", display_name: "Dana Sosial", is_active: true, percentage_allocation: 15.0, is_member_share: false },
  { feature_key: "bonus_karyawan", display_name: "Bonus Karyawan", is_active: true, percentage_allocation: 15.0, is_member_share: false },
  { feature_key: "bonus_pimpinan_gerai", display_name: "Bonus Pimpinan Gerai", is_active: true, percentage_allocation: 15.0, is_member_share: false },
  { feature_key: "shu_anggota", display_name: "SHU Anggota", is_active: true, percentage_allocation: 25.0, is_member_share: true },
];

const STORAGE_KEY = "kd_org_profit_config_v2";
const ENABLE_KEY = "kd_shu_enabled_v1";

const SHUContext = createContext(null);

export function SHUConfigProvider({ children }) {
  const [enabled, setEnabledState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(ENABLE_KEY)) ?? true; } catch { return true; }
  });
  const [config, setConfig] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return stored && Array.isArray(stored) && stored.length > 0 ? stored : DEFAULT_ORG_PROFIT_CONFIG;
    } catch { return DEFAULT_ORG_PROFIT_CONFIG; }
  });
  const [year, setYear] = useState(2026);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(config)); }, [config]);
  useEffect(() => { localStorage.setItem(ENABLE_KEY, JSON.stringify(enabled)); }, [enabled]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) setConfig(JSON.parse(e.newValue));
      if (e.key === ENABLE_KEY && e.newValue) setEnabledState(JSON.parse(e.newValue));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const updateRow = useCallback((feature_key, patch) => {
    setConfig((arr) => arr.map((r) => r.feature_key === feature_key ? { ...r, ...patch } : r));
  }, []);

  // Ganti seluruh daftar kategori — dipakai saat memuat dari backend & setelah CRUD tambah/hapus.
  const setAll = useCallback((rows) => {
    if (Array.isArray(rows) && rows.length) {
      setConfig(rows.map((r) => ({
        feature_key: r.feature_key,
        display_name: r.display_name || metaFor(r.feature_key).name,
        is_active: !!r.is_active,
        percentage_allocation: Number(r.percentage_allocation) || 0,
        is_member_share: !!r.is_member_share,
      })));
    }
  }, []);

  const resetDefault = useCallback(() => setConfig(DEFAULT_ORG_PROFIT_CONFIG), []);
  const setEnabled = useCallback((v) => setEnabledState(v), []);

  const totalPct = config.reduce((s, r) => s + (r.is_active ? Number(r.percentage_allocation) : 0), 0);
  const memberPct = config.reduce((s, r) => s + (r.is_active && r.is_member_share ? Number(r.percentage_allocation) : 0), 0);

  return (
    <SHUContext.Provider value={{ enabled, setEnabled, config, updateRow, setAll, resetDefault, year, setYear, totalPct, memberPct }}>
      {children}
    </SHUContext.Provider>
  );
}

export const useSHUConfig = () => {
  const ctx = useContext(SHUContext);
  if (!ctx) throw new Error("useSHUConfig must be used within SHUConfigProvider");
  return ctx;
};
